import { useEffect, useMemo, useCallback } from "react";
import { Common } from "@/components";
import MappingTable from "./MappingTable";
import { useImportOrder } from "../providers/ImportOrderContext";
import { useNavigate } from "react-router-dom";
import { ImportMethod, DocumentType, AddressMode } from "../models";
import {
  salesFields,
  ordersFields,
  invoicesFields,
  addressMultiLineFields,
  addressSingleLineFields,
} from "../configVariable";
import { getMappingSetKey, normalizeColumnName } from "../utils";

const MappingFields = () => {
  const { state, dispatch, handleCancel } = useImportOrder();
  const navigate = useNavigate();

  const handleToggleAddressMode = useCallback(() => {
    // Switch the address mode
    const newAddressMode =
      state.addressMode === AddressMode.SINGLE_LINE
        ? AddressMode.MULTI_LINE
        : AddressMode.SINGLE_LINE;

    dispatch({
      type: "SET_IMPORT_OPTIONS",
      payload: {
        importMethod: state.importMethod,
        documentType: state.documentType,
        addressMode: newAddressMode,
        taxType: state.taxType,
      },
    });
  }, [
    state.addressMode,
    state.importMethod,
    state.documentType,
    state.taxType,
    dispatch,
  ]);

  const mappingFields = useMemo(() => {
    const fields = [
      ...(state.documentType === DocumentType.ORDER
        ? ordersFields
        : invoicesFields),
    ];

    if (state.addressMode === AddressMode.SINGLE_LINE) {
      fields.push(...addressSingleLineFields);
    } else {
      fields.push(...addressMultiLineFields);
    }

    if (state.importMethod === ImportMethod.HEADERS_AND_DETAILS) {
      fields.push(...salesFields);
    }

    return fields;
  }, [state.importMethod, state.documentType, state.addressMode]);

  const mappingSetKey = getMappingSetKey(
    state.importMethod,
    state.documentType
  );

  useEffect(() => {
    // Store the field configs in the context state
    const fieldConfigsMap = mappingFields.reduce((acc, field) => {
      acc[field.key] = field;
      return acc;
    }, {} as { [key: string]: (typeof mappingFields)[0] });

    dispatch({
      type: "SET_FIELD_CONFIGS",
      payload: fieldConfigsMap,
    });
  }, [dispatch, mappingFields]);

  useEffect(() => {
    // Redirect if no file or columns
    if (
      !state.selectedFile ||
      state.fileColumns.length === 0 ||
      !mappingSetKey
    ) {
      navigate(".", { replace: true });
      return;
    }

    let matchFound = false;
    if (
      state.preloadedMappings[mappingSetKey] &&
      state.preloadedMappings[mappingSetKey]!.length > 0
    ) {
      let bestMappingSet = null;
      let maxMatchedColumns = 0;
      let bestMapping = {} as { [key: string]: string };

      // Check each preloaded mapping set
      for (const mappingSet of state.preloadedMappings[mappingSetKey]!) {
        // Calculate the potential mapping for this set
        const potentialMapping = mappingFields.reduce((acc, field) => {
          // Get the expected column name from the mapping set
          const expectedColumnName = mappingSet[field.key];
          if (expectedColumnName) {
            // Try to find a matching column in our current file
            const matchedColumn = state.fileColumns.find(
              (col) =>
                normalizeColumnName(col) ===
                normalizeColumnName(expectedColumnName)
            );

            if (matchedColumn) {
              acc[field.key] = matchedColumn;
            }
          }
          return acc;
        }, {} as { [key: string]: string });

        // Count how many columns could be mapped
        const matchCount = Object.keys(potentialMapping).length;

        // If this mapping set has more matches than our previous best, update our best mapping
        if (matchCount > 0 && matchCount > maxMatchedColumns) {
          maxMatchedColumns = matchCount;
          bestMappingSet = mappingSet;
          bestMapping = potentialMapping;
        }
      }

      // If we found a mapping set with at least one match, use it
      if (bestMappingSet && Object.keys(bestMapping).length > 0) {
        dispatch({
          type: "SET_MAPPING",
          payload: bestMapping,
        });
        matchFound = true;
      }
    }

    // If no preloaded mapping was found, fall back to simple auto-matching
    if (!matchFound) {
      // Auto-match columns on initial load based on exact field names
      const autoMatchedFields = mappingFields.reduce((acc, field) => {
        // Try to find exact match (case insensitive)
        const matchedColumn = state.fileColumns.find(
          (col) => normalizeColumnName(col) === normalizeColumnName(field.label)
        );

        if (matchedColumn) {
          acc[field.key] = matchedColumn;
        }
        return acc;
      }, {} as { [key: string]: string });

      if (Object.keys(autoMatchedFields).length > 0) {
        dispatch({
          type: "SET_MAPPING",
          payload: autoMatchedFields,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.selectedFile,
    state.fileColumns,
    state.importMethod,
    state.taxType,
    state.preloadedMappings,
    mappingSetKey,
  ]);

  const handleMapping = (key: string, value: string) => {
    dispatch({
      type: "SET_MAPPING",
      payload: value
        ? { ...state.mappingFields, [key]: value }
        : Object.fromEntries(
            Object.entries(state.mappingFields).filter(([k]) => k !== key)
          ),
    });
  };

  return (
    <div className="relative">
      <div className="h-[calc(100vh-258px)]">
        <div className="mb-4 text-hd5 font-normal">
          We need your help matching some of the data from your CSV. Please
          map/correct any columns that we&apos;ve missed!
        </div>

        <MappingTable
          fields={mappingFields}
          fileColumns={state.fileColumns}
          mappingData={state.mappingFields}
          onMapping={handleMapping}
          taxType={state.taxType}
          addressMode={state.addressMode}
          onToggleAddressMode={handleToggleAddressMode}
        />
      </div>

      <div className="flex justify-end space-x-3 mt-5">
        <Common.Button
          color="transparent"
          onClick={() => handleCancel({ mode: "cancel", needConfirm: true })}
        >
          Cancel
        </Common.Button>
        <Common.Button
          onClick={() => {
            navigate("#upload-progress");
          }}
          disabled={mappingFields.some(
            (f) => f.required && !state.mappingFields[f.key]
          )}
        >
          Next Step
        </Common.Button>
      </div>
    </div>
  );
};

export default MappingFields;
